import { useState, useEffect, useCallback } from 'react';
import { Menu, LogOut, Shield, User as UserIcon, FileText, Briefcase, Compass, Plus, MessageSquare, Users, Bell, Home, Settings, ChevronDown, Eye, RefreshCw, HelpCircle } from 'lucide-react';
import HelpAndTips from '@/components/HelpAndTips';
import { openHelp as openHelpTips, resetHelp as resetHelpState } from '@/lib/helpStore';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const NOTIFICATION_POLL_INTERVAL = 30000;

export function getDashboardRouteForRole(role) {
  switch (role) {
    case 'admin': return '/dashboard';
    case 'regional_admin': return '/regional-admin';
    case 'employer': return '/employer/dashboard';
    default: return '/dashboard';
  }
}

function Layout({ children, user }) {
  const [open, setOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [regionBranding, setRegionBranding] = useState(null); // { name, logo_url }
  const navigate = useNavigate();
  const location = useLocation();

  const fetchUnreadCount = useCallback(async () => {
    if (!user || user.role === 'admin') return;
    try {
      const response = await axios.get(`${BACKEND_URL}/api/messages/unread-count`, { withCredentials: true });
      setUnreadCount(response.data.unread_threads || 0);
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  }, [user]);

  useEffect(() => {
    if (user && user.role !== 'admin') {
      fetchUnreadCount();
      const interval = setInterval(fetchUnreadCount, NOTIFICATION_POLL_INTERVAL);
      return () => clearInterval(interval);
    }
  }, [user, fetchUnreadCount]);

  // Fetch region branding for employers and regional admins (name shown even without a logo)
  useEffect(() => {
    if (user?.region_id && (user?.role === 'employer' || user?.role === 'regional_admin')) {
      axios.get(`${BACKEND_URL}/api/regions/${user.region_id}/branding`, { withCredentials: true })
        .then(res => {
          if (res.data && (res.data.name || res.data.logo_url)) {
            setRegionBranding(res.data);
          }
        })
        .catch(() => {});
    }
  }, [user]);

  const handleLogout = async () => {
    try {
      // Sign out of Supabase (clears Supabase session/tokens)
      await supabase.auth.signOut();
      // Clear the backend MongoDB session / cookie
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
      toast.success('Logged out successfully');
    } catch {
      // Even if one fails, navigate away
    }
    resetHelpState(); // clear in-memory help state so the next account starts clean
    navigate('/');
  };

  const handleLogoClick = () => {
    if (!user) { navigate('/'); return; }
    const route = getDashboardRouteForRole(user.role, user.employer_status);
    if (location.pathname !== route) navigate(route);
  };

  const getMenuItems = () => {
    const items = [];
    // Role-flicker guard: when the user object hasn't hydrated yet (Layout
    // remounts on every route change while each page re-fetches /api/auth/me),
    // user.role is briefly undefined. Returning [] keeps the nav empty for
    // that one frame instead of falling through to the candidate default —
    // which previously flashed job-seeker items for employers / admins /
    // regional_admins during route changes.
    if (!user?.role) return items;
    if (user.role === 'admin') {
      // Admin header nav intentionally empty — Homepage Editor lives under
      // Administration and SkillBridge Sync under Operations inside the Admin shell.
      return items;
    }
    if (user.role === 'employer') {
      items.push(
        { icon: Home, label: 'Overview', path: '/employer/dashboard', testId: 'nav-employer-dashboard' },
        { icon: Users, label: 'Find Talent', path: '/candidates/search', testId: 'nav-candidate-search' },
        { icon: Briefcase, label: 'Jobs', path: '/employer/jobs', testId: 'nav-jobs-link' },
        { icon: Plus, label: 'Post a Role', path: '/jobs/post', testId: 'nav-post-job', action: true },
        { icon: MessageSquare, label: 'Conversations', path: '/messages', testId: 'nav-messages' }
      );
      return items;
    }
    if (user.role === 'regional_admin') {
      items.push(
        { icon: Home, label: 'Region Overview', path: '/regional-admin', testId: 'nav-regional-dashboard' }
      );
      return items;
    }
    // Explicit job_seeker check (instead of a fallthrough default) so any
    // future / unknown role string never accidentally renders candidate nav.
    if (user.role === 'job_seeker') {
      items.push(
        { icon: Home, label: 'Transition', path: '/dashboard', testId: 'nav-dashboard' },
        { icon: FileText, label: 'Resume', path: '/resume-builder', testId: 'nav-resume-builder' },
        { icon: Compass, label: 'Opportunities', path: '/jobs', testId: 'nav-job-board' },
        { icon: MessageSquare, label: 'Conversations', path: '/messages', testId: 'nav-messages' }
      );
    }
    return items;
  };

  const menuItems = getMenuItems();
  const navItems = menuItems.filter((i) => !i.action);   // destinations rendered as nav
  const actionItem = menuItems.find((i) => i.action);     // e.g. employer "Post a Role" → button
  const isActive = (path) => location.pathname === path;
  // Phase 1A: Sign-out lives only inside the avatar dropdown for all signed-in users.
  const useDropdownNav = !!user && user.role !== 'admin';
  const showSettingsItem = user?.role === 'employer' || user?.role === 'regional_admin';

  const getUserDisplayName = () => {
    if (!user) return 'Account';
    if (user.name) {
      const first = user.name.split(' ')[0];
      if (first) return first;
    }
    if (user.email) return user.email.split('@')[0];
    return 'Account';
  };

  const getSettingsPath = () => {
    if (user?.role === 'regional_admin') return '/regional-admin/settings';
    return '/employer/settings';
  };

  const NotificationBell = ({ className = '' }) => (
    <Button
      data-testid="notification-bell-btn"
      onClick={() => navigate('/messages')}
      variant="ghost"
      size="icon"
      className={`relative ${className}`}
    >
      <Bell className="w-5 h-5" />
      {unreadCount > 0 && (
        <span
          data-testid="unread-badge"
          className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1"
        >
          {unreadCount > 99 ? '99+' : unreadCount}
        </span>
      )}
    </Button>
  );

  return (
    <div className="min-h-screen bg-[#f7f7f7] print:bg-white print:min-h-0">
      {/* Top Navbar — white background, olive green accents */}
      <nav className="bg-white border-b border-[#e5e5e5] sticky top-0 z-50 print:hidden" data-testid="app-navbar">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-[68px]">
            {/* Left: logo + hamburger */}
            <div className="flex items-center gap-3 min-w-0">
              <Sheet open={open} onOpenChange={setOpen}>
                <SheetTrigger asChild>
                  <Button
                    data-testid="hamburger-menu-btn"
                    variant="ghost"
                    size="icon"
                    aria-label="Open navigation menu"
                    aria-expanded={open}
                    className="text-[#444] hover:bg-[#f0f0f0] lg:hidden"
                  >
                    <Menu className="w-5 h-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-64 bg-white">
                  <SheetHeader>
                    <SheetTitle className="flex items-center gap-2 text-[#222]">
                      <Shield className="w-5 h-5 text-brand" />
                      <span>Sergeant TAP</span>
                    </SheetTitle>
                  </SheetHeader>
                  <div className="mt-8 space-y-1">
                    {menuItems.map((item) => {
                      const Icon = item.icon;
                      return (
                        <button
                          key={item.path}
                          data-testid={item.testId}
                          onClick={() => { navigate(item.path); setOpen(false); }}
                          className={`w-full flex items-center px-3 py-2 rounded-md text-left transition-colors ${
                            isActive(item.path) ? 'bg-brand-light text-brand' : 'text-[#1f2937] hover:bg-[#f5f5f5] hover:text-brand'
                          }`}
                          style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: isActive(item.path) ? 700 : 600, fontSize: 15, letterSpacing: '0.02em' }}
                        >
                          <Icon className="w-4 h-4 mr-3" />
                          {item.label}
                        </button>
                      );
                    })}
                    <div className="pt-4 border-t border-[#eee] mt-4">
                      <div className="flex items-center gap-3 px-3 py-2 mb-2">
                        <UserIcon className="w-5 h-5 text-[#999]" />
                        <div>
                          <p className="text-sm font-medium text-[#222]">{user?.name}</p>
                          <p className="text-xs text-[#999]">{user?.email}</p>
                        </div>
                      </div>
                      <button
                        data-testid="mobile-help-btn"
                        onClick={() => { openHelpTips(); setOpen(false); }}
                        className="w-full flex items-center px-3 py-2 rounded-md text-[#1f2937] hover:bg-[#f5f5f5] hover:text-brand transition-colors"
                        style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 600, fontSize: 15, letterSpacing: '0.02em' }}
                      >
                        <HelpCircle className="w-4 h-4 mr-3" />
                        Help &amp; Tips
                      </button>
                      <button
                        data-testid="mobile-logout-btn"
                        onClick={handleLogout}
                        className="w-full flex items-center px-3 py-2 rounded-md text-red-600 hover:bg-red-50 transition-colors"
                        style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 600, fontSize: 15, letterSpacing: '0.02em' }}
                      >
                        <LogOut className="w-4 h-4 mr-3" />
                        Sign out
                      </button>
                    </div>
                  </div>
                </SheetContent>
              </Sheet>

              <button
                onClick={handleLogoClick}
                className="flex items-center gap-3 hover:opacity-80 transition-opacity"
                data-testid="logo-home-btn"
              >
                {regionBranding && (regionBranding.logo_url || regionBranding.name) ? (
                  <div className="flex items-center gap-3 min-w-0" data-testid="regional-header-lockup">
                    {regionBranding.logo_url ? (
                      <img
                        src={regionBranding.logo_url.startsWith('data:') ? regionBranding.logo_url : `${BACKEND_URL}${regionBranding.logo_url}`}
                        alt={regionBranding.name || 'Region'}
                        data-testid="region-logo-img"
                        className="max-w-[115px] max-h-[50px] w-auto h-auto object-contain flex-shrink-0"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <span
                        data-testid="region-logo-fallback"
                        className="flex-shrink-0 bg-brand-light text-brand font-extrabold text-lg px-3 py-1.5 rounded-lg border border-brand/20 leading-none"
                      >
                        {(regionBranding.name || 'R').split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase()}
                      </span>
                    )}
                    <div className="hidden sm:flex flex-col justify-center min-w-0 leading-tight">
                      <span className="text-sm sm:text-base font-extrabold text-slate-900 truncate max-w-[200px] lg:max-w-[260px]">
                        {regionBranding.name || 'Workforce Network'}
                      </span>
                      <span className="hidden md:inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-brand mt-0.5">
                        <span className="bg-brand-light px-1.5 py-0.5 rounded">Powered by Sergeant TAP</span>
                      </span>
                    </div>
                  </div>
                ) : (
                  <img
                    src={`${process.env.PUBLIC_URL}/sergeant-tap-logo.png`}
                    alt="Sergeant TAP"
                    className="h-9 sm:h-10 w-auto object-contain"
                    data-testid="header-logo"
                  />
                )}
              </button>
            </div>

            {/* Desktop Navigation — left-aligned next to the logo */}
            <div className="hidden lg:flex flex-1 justify-start items-center gap-1 ml-6">
              {navItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                return (
                  <button
                    key={item.path}
                    data-testid={item.testId}
                    onClick={() => navigate(item.path)}
                    aria-current={active ? 'page' : undefined}
                    className={`nav-header-link inline-flex items-center whitespace-nowrap px-3 py-2 rounded-md transition-colors ${
                      active
                        ? 'bg-brand-light text-brand'
                        : 'text-[#4b5563] hover:text-brand hover:bg-[#f5f5f5]'
                    }`}
                    style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: active ? 700 : 500, fontSize: 14, letterSpacing: '0.01em' }}
                  >
                    <Icon className="w-4 h-4 mr-1.5" />
                    {item.label}
                  </button>
                );
              })}
            </div>

            {/* Desktop right actions — action button + bell + profile */}
            <div className="hidden lg:flex items-center gap-1.5 flex-shrink-0">
              {actionItem && (
                <button
                  data-testid={actionItem.testId}
                  onClick={() => navigate(actionItem.path)}
                  className="inline-flex items-center whitespace-nowrap gap-1.5 px-3.5 py-2 rounded-md bg-brand text-white hover:bg-brand/90 transition-colors mr-1"
                  style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 600, fontSize: 14, letterSpacing: '0.01em' }}
                >
                  <Plus className="w-4 h-4" />
                  {actionItem.label}
                </button>
              )}
              {user?.role !== 'admin' && (
                <NotificationBell className="text-[#555] hover:bg-[#f5f5f5]" />
              )}

              {useDropdownNav ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      data-testid="user-menu-btn"
                      className="nav-header-link inline-flex items-center px-2.5 py-1.5 rounded-full text-[#1f2937] hover:bg-[#f5f5f5] ml-1 transition-colors gap-2"
                      style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 600, fontSize: 15, letterSpacing: '0.02em' }}
                    >
                      <span className="w-7 h-7 rounded-full bg-brand-light flex items-center justify-center">
                        <UserIcon className="w-4 h-4 text-brand" />
                      </span>
                      {getUserDisplayName()}
                      <ChevronDown className="w-3.5 h-3.5 opacity-50" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    {user?.role === 'job_seeker' && (
                      <>
                        <DropdownMenuItem
                          data-testid="user-menu-visibility"
                          onClick={() => navigate('/profile/visibility')}
                          className="cursor-pointer"
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          Visibility
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                      </>
                    )}
                    {showSettingsItem && (
                      <>
                        <DropdownMenuItem
                          data-testid="user-menu-settings"
                          onClick={() => navigate(getSettingsPath())}
                          className="cursor-pointer"
                        >
                          <Settings className="w-4 h-4 mr-2" />
                          Settings
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                      </>
                    )}
                    <DropdownMenuItem
                      data-testid="user-menu-help"
                      onClick={() => openHelpTips()}
                      className="cursor-pointer"
                    >
                      <HelpCircle className="w-4 h-4 mr-2" />
                      Help &amp; Tips
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      data-testid="user-menu-logout"
                      onClick={handleLogout}
                      className="cursor-pointer text-red-600 focus:text-red-600"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Sign out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <button
                  data-testid="desktop-logout-btn"
                  onClick={handleLogout}
                  className="nav-header-link inline-flex items-center px-3 py-2 rounded-md text-[#999] hover:text-red-600 hover:bg-red-50 ml-1 transition-colors"
                  style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 600, fontSize: 15, letterSpacing: '0.02em' }}
                >
                  <LogOut className="w-4 h-4 mr-1.5" />
                  Sign out
                </button>
              )}
            </div>

            {/* Mobile: Bell */}
            <div className="flex lg:hidden items-center gap-2">
              {user?.role !== 'admin' && (
                <NotificationBell className="text-[#555] hover:bg-[#f5f5f5]" />
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main>{children}</main>
      {user && <HelpAndTips user={user} />}
    </div>
  );
}

export default Layout;
